// ModelDropdown.jsx — Combobox replacement for the native <select>.
//
// Por que: <select> nativo HTML não renderiza CSS/HTML dentro de <option>
// (Chromium/Electron). Bolinha de status era emoji 🟢 GIGANTE, sem glow.
//
// Solução /shad-pro + /tastedesign + /microinteractions-mastery:
//  - Trigger button compacto que mostra o modelo atual (dot + label + ▾)
//  - Popover absolute + Framer Motion spring (380/32)
//  - cmdk pra search + keyboard nav (↑↓, Enter, Esc)
//  - Cada item: dot 6px com box-shadow glow + label + badge "custom"
//  - Group header colorido por provider
//  - Click-outside fecha, Esc fecha, focus volta pro trigger
//  - Accessibility: combobox role + aria-expanded + aria-controls

import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Command } from 'cmdk';
import { Check, ChevronDown, Search } from 'lucide-react';
import ModelStatusDot from '../shared/ModelStatusDot.jsx';
import { tierLabel } from '../../lib/modelTiers.js';

const SPRING = { type: 'spring', stiffness: 380, damping: 32, mass: 0.6 };

// Limpa o prefixo 🟢 do label (legado — useUnifiedModels já retorna limpo,
// mas mantém no-op defensivo pra ids salvos antigos).
function stripEmoji(s) {
  return typeof s === 'string' ? s.replace(/^🟢\s*/, '') : s;
}

// StatusDot agora é o componente compartilhado ModelStatusDot.
function StatusDot({ connected }) {
  return <ModelStatusDot connected={connected} size={6} />;
}

export default function ModelDropdown({ value, onChange, groups, className, triggerClassName }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Posição absoluta do popover (fixed) — calculada do trigger via
  // getBoundingClientRect. Necessário pra escapar de ancestors com
  // overflow:hidden (SessionWorkspace tem 3 níveis). Portal pro body.
  //
  // Auto-flip: se não cabe pra baixo (composer bottom), abre pra cima.
  const [pos, setPos] = useState({ top: 0, bottom: null, right: 0, openUp: false });
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const listboxId = useId();

  // Recalcula posição quando abre OU em scroll/resize enquanto aberto.
  // useLayoutEffect pra evitar flash na primeira frame.
  useLayoutEffect(() => {
    if (!open) return undefined;
    function updatePos() {
      const el = triggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const popoverMaxHeight = 420;
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      const triggerCenter = (r.top + r.bottom) / 2;
      // Abre pra cima se:
      // (a) trigger está na metade inferior da viewport (regra intuitiva —
      //     composer fica sempre embaixo, então dropdown sobe), OU
      // (b) não cabe embaixo e cabe melhor em cima (fallback).
      const openUp =
        triggerCenter > window.innerHeight * 0.5
        || (spaceBelow < popoverMaxHeight && spaceAbove > spaceBelow);
      setPos({
        top: openUp ? null : r.bottom + 4,
        bottom: openUp ? window.innerHeight - r.top + 4 : null,
        right: window.innerWidth - r.right,
        openUp,
      });
    }
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open]);

  // Flatten pra resolver current selection rápido
  const currentModel = useMemo(() => {
    for (const g of groups) {
      const hit = g.models.find((m) => m.id === value);
      if (hit) return { ...hit, provider: g.provider };
    }
    return null;
  }, [groups, value]);

  // Click outside fecha
  useEffect(() => {
    if (!open) return undefined;
    function onClick(e) {
      if (!popoverRef.current || !triggerRef.current) return;
      if (
        popoverRef.current.contains(e.target) ||
        triggerRef.current.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Esc fecha + foca trigger de volta
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  function handlePick(id) {
    onChange(id);
    setOpen(false);
    setQuery('');
    requestAnimationFrame(() => triggerRef.current?.focus());
  }

  return (
    <div className={'relative inline-block ' + (className || '')}>
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-haspopup="listbox"
        aria-label="Selecionar modelo"
        onClick={() => setOpen((v) => !v)}
        /* (Pedro 2026-06-25, sprint cursor-strip-4 round 3) — triggerClassName
           deixa o composer passar `rx-cpill` pra o GLM 5.2 virar uma pill igual
           aos vizinhos (Plan / Add context / Ref / Auto-aprovar). Sem prop,
           mantém o trigger compacto padrão. max-w-[200px] preserva o truncate. */
        className={
          triggerClassName
            ? `${triggerClassName} max-w-[200px]`
            : 'inline-flex h-6 max-w-[200px] items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2/40 px-2 text-apolo-textPrimary transition hover:bg-apolo-surface2 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50'
        }
      >
        <StatusDot connected={currentModel?.connected ?? true} />
        <span className="truncate text-xs">
          {stripEmoji(currentModel?.label) || value || 'Selecionar…'}
        </span>
        <ChevronDown
          size={12}
          aria-hidden="true"
          className={
            'shrink-0 text-apolo-textTertiary transition-transform ' +
            (open ? 'rotate-180' : '')
          }
        />
      </button>

      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {open && (
          <motion.div
            ref={popoverRef}
            id={listboxId}
            role="listbox"
            initial={{ opacity: 0, y: pos.openUp ? 4 : -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: pos.openUp ? 4 : -4, scale: 0.97 }}
            transition={SPRING}
            className="fixed z-[100] w-[320px] overflow-hidden rounded-xl border border-apolo-borderSubtle bg-apolo-surface1 shadow-[0_24px_64px_-12px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.04)] backdrop-blur-md"
            style={{
              transformOrigin: pos.openUp ? 'bottom right' : 'top right',
              top: pos.top ?? undefined,
              bottom: pos.bottom ?? undefined,
              right: pos.right,
            }}
          >
            <Command
              shouldFilter={query.trim().length > 0}
              className="flex flex-col"
            >
              <div className="flex items-center gap-2 border-b border-apolo-borderSubtle px-3 py-2">
                <Search
                  size={12}
                  aria-hidden="true"
                  className="shrink-0 text-apolo-textTertiary"
                />
                <Command.Input
                  value={query}
                  onValueChange={setQuery}
                  placeholder="Buscar modelo ou adapter…"
                  autoFocus
                  className="flex-1 bg-transparent text-xs text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
                />
              </div>
              <Command.List className="max-h-[360px] overflow-y-auto p-1">
                <Command.Empty className="px-3 py-6 text-center text-xs text-apolo-textTertiary">
                  Nenhum modelo encontrado.
                </Command.Empty>
                {groups.map((g) => (
                  <Command.Group
                    key={g.provider}
                    heading={g.provider}
                    className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-apolo-textTertiary"
                  >
                    {g.models.map((m) => {
                      const isSelected = m.id === value;
                      const clean = stripEmoji(m.label);
                      const isCustom = g.provider.includes('· custom');
                      // value: ID único + label searchable. Sem caracteres
                      // especiais (·) que confundem o matcher do cmdk.
                      const cmdkValue = `${m.id} ${clean}`.toLowerCase();
                      return (
                        <Command.Item
                          key={m.id}
                          value={cmdkValue}
                          onSelect={() => handlePick(m.id)}
                          className={
                            'group flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-xs transition data-[selected=true]:bg-apolo-surface2 ' +
                            (isSelected
                              ? 'bg-apolo-surface2 text-apolo-textPrimary'
                              : 'text-apolo-textSecondary hover:bg-apolo-surface2')
                          }
                        >
                          <StatusDot connected={m.connected} />
                          <span className="flex-1 truncate">{clean}</span>
                          {m.tier && tierLabel(m.tier) && (
                            <span
                              aria-hidden="true"
                              title={`Tier: ${tierLabel(m.tier)}`}
                              className="shrink-0 rounded-sm border border-apolo-borderSubtle bg-apolo-surface1 px-1 py-px text-[9px] font-medium uppercase tracking-wider text-apolo-textTertiary"
                            >
                              {tierLabel(m.tier)}
                            </span>
                          )}
                          {isCustom && (
                            <span
                              aria-hidden="true"
                              className="rounded-sm border border-violet-500/30 bg-violet-500/10 px-1 py-px text-[9px] font-medium uppercase tracking-wider text-violet-300"
                            >
                              custom
                            </span>
                          )}
                          {isSelected && (
                            <Check
                              size={11}
                              className="shrink-0 text-apolo-accent"
                              aria-hidden="true"
                            />
                          )}
                        </Command.Item>
                      );
                    })}
                  </Command.Group>
                ))}
              </Command.List>
              <div className="flex items-center justify-end gap-3 border-t border-apolo-borderSubtle px-3 py-1.5 text-[10px] text-apolo-textTertiary">
                <span>
                  <kbd className="rounded bg-apolo-surface2 px-1 text-[9px]">↑↓</kbd> navegar
                </span>
                <span>
                  <kbd className="rounded bg-apolo-surface2 px-1 text-[9px]">↵</kbd> selecionar
                </span>
                <span>
                  <kbd className="rounded bg-apolo-surface2 px-1 text-[9px]">esc</kbd> fechar
                </span>
              </div>
            </Command>
          </motion.div>
        )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  );
}
